/**
 * DocInsightsPage — TechDocs analytics tab for catalog entities.
 *
 * Supports drill-down navigation:
 *   Main view  →  Page detail  →  User detail  (and back)
 *   Main view  →  User detail  →  Page detail  (and back)
 *
 * Date range filter and page search apply to the main view.
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCard, Progress, Link } from '@backstage/core-components';
import Box from '@mui/material/Box';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { People as PeopleIcon, Person as PersonIcon, Search as SearchIcon, TrendingUp as TrendingUpIcon, Visibility as VisibilityIcon } from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FetchFn = <T>(path: string, params?: Record<string, string>) => Promise<T>;

type EntityParams = { kind: string; namespace: string; name: string };

type Summary = {
  totalViews: number;
  uniquePages: number;
  uniqueVisitors: number;
  lastVisited: string | null;
};

type PageStat = {
  pagePath: string;
  views: number;
  uniqueVisitors: number;
  lastVisited: string | null;
};

type PagesResponse = {
  pages: PageStat[];
  total: number;
  page: number;
  pageSize: number;
};

type Visitor = {
  userRef: string;
  pagePath: string;
  visitedAt: string;
};

type MonthlyStat = {
  month: string;
  views: number;
  uniqueVisitors: number;
};

type PageVisitorRow = {
  userRef: string;
  totalVisits: number;
  firstVisit: string;
  lastVisit: string;
};

type PageMonthlyStat = {
  month: string;
  views: number;
};

type AlsoReadRow = {
  pagePath: string;
  sharedVisitors: number;
};

type TopViewerRow = {
  userRef: string;
  totalVisits: number;
  uniquePages: number;
  firstVisit: string | null;
  lastVisit: string | null;
};

type UserSummary = {
  totalVisits: number;
  uniquePages: number;
  firstVisit: string | null;
  lastVisit: string | null;
};

type UserPageRow = {
  pagePath: string;
  totalVisits: number;
  firstVisit: string;
  lastVisit: string;
};

type VisitorsResponse = {
  visitors: Visitor[];
  total: number;
  page: number;
  pageSize: number;
};

type PageVisitorsResponse = {
  visitors: PageVisitorRow[];
  total: number;
  totalViews: number;
  uniqueVisitors: number;
  lastVisit: string | null;
  page: number;
  pageSize: number;
};

type UserPagesResponse = {
  pages: UserPageRow[];
  total: number;
  page: number;
  pageSize: number;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Debounces a value — delays propagating updates until the user stops typing. */
function useDebounce<T,>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

function displayUser(userRef: string): string {
  return userRef.replace(/^user:[^/]+\//, '');
}

function displayPage(pagePath: string): string {
  return pagePath.replace(/^\//, '') || '/';
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function SummaryCard({
  icon,
  label,
  value,
  colour,
}: {
  icon: React.ReactElement;
  label: string;
  value: React.ReactNode;
  colour: string;
}) {
  return (
    <Paper variant="outlined" style={{ padding: '20px 24px', height: '100%' }}>
      <Box display="flex" alignItems="center" mb={1}>
        <Box mr={1.5} style={{ color: colour, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="caption" style={{ color: '#757575', textTransform: 'uppercase', letterSpacing: 1 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="h5" style={{ fontWeight: 700 }}>
        {value}
      </Typography>
    </Paper>
  );
}

function MonthlyBar({ views, maxViews, label }: { views: number; maxViews: number; label: string }) {
  const pct = maxViews > 0 ? Math.max((views / maxViews) * 100, views > 0 ? 2 : 0) : 0;
  return (
    <Box display="flex" alignItems="center" mb={0.75}>
      <Typography variant="caption" style={{ width: 68, flexShrink: 0, color: '#555' }}>
        {label}
      </Typography>
      <Box flexGrow={1} mx={1} style={{ background: '#f0f0f0', borderRadius: 4, height: 18, overflow: 'hidden' }}>
        <Box
          style={{
            width: `${pct}%`,
            height: '100%',
            background: '#1976d2',
            borderRadius: 4,
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
      <Typography variant="caption" style={{ width: 52, flexShrink: 0, textAlign: 'right', color: '#555' }}>
        {views.toLocaleString()}
      </Typography>
    </Box>
  );
}

function DocBreadcrumbs({
  crumbs,
  onBack,
}: {
  crumbs: { label: string; onClick?: () => void }[];
  onBack: () => void;
}) {
  return (
    <Box display="flex" alignItems="center" mb={3} style={{ gap: 8 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        size="small"
        style={{ minWidth: 0, paddingRight: 8 }}
        aria-label="Go back"
      />
      <MuiBreadcrumbs aria-label="breadcrumb">
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return isLast ? (
            <Typography
              key={i}
              variant="body2"
              color="text.primary"
              style={{ fontWeight: 600 }}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Typography
              key={i}
              variant="body2"
              color="text.secondary"
              onClick={crumb.onClick}
              style={crumb.onClick ? { cursor: 'pointer' } : undefined}
            >
              {crumb.label}
            </Typography>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PageDetailView
// ---------------------------------------------------------------------------

function PageDetailView({
  pagePath,
  entity,
  apiFetch,
  onNavigateUser,
  onBack,
  onGoToMain,
  entityName,
}: {
  pagePath: string;
  entity: EntityParams;
  apiFetch: FetchFn;
  onNavigateUser: (userRef: string, fromPage: string) => void;
  onBack: () => void;
  onGoToMain: () => void;
  entityName: string;
}) {
  const { kind, namespace, name } = entity;
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const [visitorsData, setVisitorsData] = useState<PageVisitorsResponse | null>(null);
  const [visitorsLoading, setVisitorsLoading] = useState(true);
  const [visitorSearch, setVisitorSearch] = useState('');
  const [visitorPage, setVisitorPage] = useState(1);
  const debouncedVisitorSearch = useDebounce(visitorSearch, 300);

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [monthly, setMonthly] = useState<PageMonthlyStat[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(true);

  const [alsoRead, setAlsoRead] = useState<AlsoReadRow[]>([]);
  const [alsoReadLoading, setAlsoReadLoading] = useState(true);

  useEffect(() => {
    setVisitorsLoading(true);
    const params: Record<string, string> = { path: pagePath, page: String(visitorPage), pageSize: '20' };
    if (debouncedVisitorSearch) params.search = debouncedVisitorSearch;
    apiFetch<PageVisitorsResponse>('page-detail/visitors', params)
      .then(data => setVisitorsData(data))
      .catch(() => setVisitorsData(null))
      .finally(() => setVisitorsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePath, debouncedVisitorSearch, visitorPage]);

  useEffect(() => {
    setAlsoReadLoading(true);
    apiFetch<AlsoReadRow[]>('page-detail/also-read', { path: pagePath })
      .then(data => setAlsoRead(data))
      .catch(() => setAlsoRead([]))
      .finally(() => setAlsoReadLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePath]);

  useEffect(() => {
    setMonthlyLoading(true);
    apiFetch<PageMonthlyStat[]>('page-detail/monthly', { path: pagePath, year: String(selectedYear) })
      .then(data => setMonthly(data))
      .catch(() => setMonthly([]))
      .finally(() => setMonthlyLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePath, selectedYear]);

  const maxMonthlyViews = Math.max(...monthly.map(m => m.views), 1);

  return (
    <Box mt={1}>
      <DocBreadcrumbs
        crumbs={[
          { label: entityName, onClick: onGoToMain },
          { label: displayPage(pagePath) },
        ]}
        onBack={onBack}
      />

      <Box mb={3}>
        <Link to={`/docs/${namespace}/${kind}/${name}${pagePath}`} style={{ fontSize: 13 }}>
          Open page in docs →
        </Link>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={4}>
          <SummaryCard icon={<VisibilityIcon />} label="Total views" value={(visitorsData?.totalViews ?? 0).toLocaleString()} colour="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard icon={<PeopleIcon />} label="Unique visitors" value={(visitorsData?.uniqueVisitors ?? 0).toLocaleString()} colour="#f57c00" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SummaryCard
            icon={<AccessTimeIcon />}
            label="Last viewed"
            value={formatDate(visitorsData?.lastVisit ?? null)}
            colour="#7b1fa2"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Visitors table */}
        <Grid item xs={12} md={7}>
          <InfoCard
            title="Visitors"
            noPadding
            action={
              <TextField
                placeholder="Search users…"
                size="small"
                value={visitorSearch}
                onChange={e => { setVisitorSearch(e.target.value); setVisitorPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: '#757575' }} />
                    </InputAdornment>
                  ),
                }}
                style={{ width: 200 }}
              />
            }
          >
            {(() => {
              if (visitorsLoading) return <Box p={2}><Progress /></Box>;
              if (!visitorsData || visitorsData.visitors.length === 0) return (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">
                    {visitorSearch ? `No visitors matching "${visitorSearch}".` : 'No visitors recorded yet.'}
                  </Typography>
                </Box>
              );
              return (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Visits</TableCell>
                      <TableCell align="right">First visit</TableCell>
                      <TableCell align="right">Last visit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visitorsData.visitors.map(v => (
                      <TableRow
                        key={v.userRef}
                        hover
                        style={{ cursor: 'pointer' }}
                        onClick={() => onNavigateUser(v.userRef, pagePath)}
                      >
                        <TableCell>
                          <Tooltip title={v.userRef}>
                            <span style={{ color: '#e8442b' }}>{displayUser(v.userRef)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">{v.totalVisits.toLocaleString()}</TableCell>
                        <TableCell align="right">{formatDate(v.firstVisit)}</TableCell>
                        <TableCell align="right">{formatDate(v.lastVisit)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                  <Typography variant="caption" color="text.secondary">
                    {(visitorsData.page - 1) * visitorsData.pageSize + 1}–{Math.min(visitorsData.page * visitorsData.pageSize, visitorsData.total)} of {visitorsData.total.toLocaleString()} visitors
                  </Typography>
                  <Box>
                    <Button size="small" disabled={visitorPage <= 1} onClick={() => setVisitorPage(p => p - 1)} startIcon={<NavigateBeforeIcon />}>Prev</Button>
                    <Button size="small" disabled={visitorPage * visitorsData.pageSize >= visitorsData.total} onClick={() => setVisitorPage(p => p + 1)} endIcon={<NavigateNextIcon />}>Next</Button>
                  </Box>
                </Box>
              </>
              );
            })()}
          </InfoCard>
        </Grid>

        {/* Monthly trend + also-read */}
        <Grid item xs={12} md={5}>
          <Box mb={3}>
            <InfoCard
              title="Views this page"
              action={
                <Box display="flex" style={{ gap: 4 }}>
                  {yearOptions.map(y => (
                    <Chip
                      key={y}
                      label={y}
                      size="small"
                      color={y === selectedYear ? 'primary' : 'default'}
                      onClick={() => setSelectedYear(y)}
                      clickable
                    />
                  ))}
                </Box>
              }
            >
              {(() => {
                if (monthlyLoading) return <Progress />;
                if (monthly.length === 0) return <Typography variant="body2" color="text.secondary">No data for {selectedYear}.</Typography>;
                return (
                <Box pt={1}>
                  {monthly.map(m => (
                    <MonthlyBar key={m.month} label={m.month.slice(5)} views={m.views} maxViews={maxMonthlyViews} />
                  ))}
                  <Divider style={{ margin: '12px 0 8px' }} />
                  <Box display="flex" alignItems="center">
                    <BarChartIcon style={{ fontSize: 14, color: '#757575', marginRight: 4 }} />
                    <Typography variant="caption" color="text.secondary">
                      {monthly.reduce((s, m) => s + m.views, 0).toLocaleString()} views in {selectedYear}
                    </Typography>
                  </Box>
                </Box>
                );
              })()}
            </InfoCard>
          </Box>

          <InfoCard title="Also read by same visitors" noPadding>
            {(() => {
              if (alsoReadLoading) return <Box p={2}><Progress /></Box>;
              if (alsoRead.length === 0) return (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">No related pages found.</Typography>
                </Box>
              );
              return (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Page</TableCell>
                    <TableCell align="right">Shared visitors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {alsoRead.map(r => (
                    <TableRow key={r.pagePath} hover>
                      <TableCell>
                        <Link to={`/docs/${namespace}/${kind}/${name}${r.pagePath}`}>
                          {displayPage(r.pagePath)}
                        </Link>
                      </TableCell>
                      <TableCell align="right">{r.sharedVisitors.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              );
            })()}
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// UserDetailView
// ---------------------------------------------------------------------------

function UserDetailView({
  userRef,
  fromPage,
  apiFetch,
  onNavigatePage,
  onBack,
  onGoToMain,
  entityName,
}: {
  userRef: string;
  fromPage?: string;
  apiFetch: FetchFn;
  onNavigatePage: (pagePath: string) => void;
  onBack: () => void;
  onGoToMain: () => void;
  entityName: string;
}) {
  const [userSummary, setUserSummary] = useState<UserSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [pagesData, setPagesData] = useState<UserPagesResponse | null>(null);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [userPageSearch, setUserPageSearch] = useState('');
  const [userPageNum, setUserPageNum] = useState(1);
  const debouncedUserPageSearch = useDebounce(userPageSearch, 300);

  useEffect(() => {
    setSummaryLoading(true);
    apiFetch<UserSummary>('user-detail/summary', { user: userRef })
      .then(data => setUserSummary(data))
      .catch(() => setUserSummary(null))
      .finally(() => setSummaryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRef]);

  useEffect(() => {
    setPagesLoading(true);
    const params: Record<string, string> = { user: userRef, page: String(userPageNum), pageSize: '20' };
    if (debouncedUserPageSearch) params.search = debouncedUserPageSearch;
    apiFetch<UserPagesResponse>('user-detail/pages', params)
      .then(data => setPagesData(data))
      .catch(() => setPagesData(null))
      .finally(() => setPagesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRef, debouncedUserPageSearch, userPageNum]);

  const displayName = displayUser(userRef);

  return (
    <Box mt={1}>
      <DocBreadcrumbs
        crumbs={[
          { label: entityName, onClick: onGoToMain },
          ...(fromPage ? [{ label: displayPage(fromPage), onClick: () => onNavigatePage(fromPage) }] : []),
          { label: displayName },
        ]}
        onBack={onBack}
      />

      {/* User header */}
      <Box mb={3} display="flex" alignItems="center">
        <PersonIcon style={{ fontSize: 40, color: '#f57c00', marginRight: 12 }} />
        <Box>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
          <Link to={`/catalog/${userRef.replace(':', '/')}`} style={{ fontSize: 13 }}>
            View catalog profile →
          </Link>
        </Box>
      </Box>

      {/* Summary cards */}
      {summaryLoading ? (
        <Box mb={3}><Progress /></Box>
      ) : userSummary && (
        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          <Grid item xs={12} sm={3}>
            <SummaryCard icon={<VisibilityIcon />} label="Total visits" value={userSummary.totalVisits.toLocaleString()} colour="#1976d2" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <SummaryCard icon={<LibraryBooksIcon />} label="Pages read" value={userSummary.uniquePages.toLocaleString()} colour="#388e3c" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <SummaryCard icon={<TrendingUpIcon />} label="First visit" value={formatDate(userSummary.firstVisit)} colour="#1976d2" />
          </Grid>
          <Grid item xs={12} sm={3}>
            <SummaryCard icon={<AccessTimeIcon />} label="Last visit" value={formatDate(userSummary.lastVisit)} colour="#7b1fa2" />
          </Grid>
        </Grid>
      )}

      {/* Pages read table */}
      <InfoCard
        title="Pages read"
        noPadding
        action={
          <TextField
            placeholder="Search pages…"
            size="small"
            value={userPageSearch}
            onChange={e => { setUserPageSearch(e.target.value); setUserPageNum(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: '#757575' }} />
                </InputAdornment>
              ),
            }}
            style={{ width: 200 }}
          />
        }
      >
        {(() => {
          if (pagesLoading) return <Box p={2}><Progress /></Box>;
          if (!pagesData || pagesData.pages.length === 0) return (
            <Box p={3}>
              <Typography variant="body2" color="text.secondary">
                {userPageSearch ? `No pages matching "${userPageSearch}".` : 'No pages recorded for this user.'}
              </Typography>
            </Box>
          );
          return (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell align="right">Visits</TableCell>
                  <TableCell align="right">First visit</TableCell>
                  <TableCell align="right">Last visit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagesData.pages.map(p => (
                  <TableRow
                    key={p.pagePath}
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => onNavigatePage(p.pagePath)}
                  >
                    <TableCell>
                      <span style={{ color: '#e8442b' }}>{displayPage(p.pagePath)}</span>
                    </TableCell>
                    <TableCell align="right">{p.totalVisits.toLocaleString()}</TableCell>
                    <TableCell align="right">{formatDate(p.firstVisit)}</TableCell>
                    <TableCell align="right">{formatDate(p.lastVisit)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
              <Typography variant="caption" color="text.secondary">
                {(pagesData.page - 1) * pagesData.pageSize + 1}–{Math.min(pagesData.page * pagesData.pageSize, pagesData.total)} of {pagesData.total.toLocaleString()} pages
              </Typography>
              <Box>
                <Button size="small" disabled={userPageNum <= 1} onClick={() => setUserPageNum(p => p - 1)} startIcon={<NavigateBeforeIcon />}>Prev</Button>
                <Button size="small" disabled={userPageNum * pagesData.pageSize >= pagesData.total} onClick={() => setUserPageNum(p => p + 1)} endIcon={<NavigateNextIcon />}>Next</Button>
              </Box>
            </Box>
          </>
          );
        })()}
      </InfoCard>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MainView
// ---------------------------------------------------------------------------

function MainView({
  apiFetch,
  entityRef,
  onNavigatePage,
  onNavigateUser,
}: {
  apiFetch: FetchFn;
  entityRef: string;
  onNavigatePage: (pagePath: string) => void;
  onNavigateUser: (userRef: string) => void;
}) {
  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Summary
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  useEffect(() => {
    setSummaryLoading(true);
    const params: Record<string, string> = {};
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    apiFetch<Summary>('summary', params)
      .then(data => { setSummary(data); setSummaryError(null); })
      .catch(e => setSummaryError(String(e)))
      .finally(() => setSummaryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef, dateFrom, dateTo]);

  // Pages table
  const PAGE_SIZE = 20;
  const [pagesData, setPagesData] = useState<PagesResponse | null>(null);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [pagesPage, setPagesPage] = useState(1);
  const [pageSearch, setPageSearch] = useState('');
  const debouncedPageSearch = useDebounce(pageSearch, 300);

  useEffect(() => {
    setPagesLoading(true);
    const params: Record<string, string> = { page: String(pagesPage), pageSize: String(PAGE_SIZE) };
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (debouncedPageSearch) params.search = debouncedPageSearch;
    apiFetch<PagesResponse>('pages', params)
      .then(data => setPagesData(data))
      .catch(() => setPagesData(null))
      .finally(() => setPagesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef, dateFrom, dateTo, pagesPage, debouncedPageSearch]);

  // Visitors
  const [visitorsData, setVisitorsData] = useState<VisitorsResponse | null>(null);
  const [visitorsLoading, setVisitorsLoading] = useState(true);
  const [visitorSearch, setVisitorSearch] = useState('');
  const [visitorsPage, setVisitorsPage] = useState(1);
  const debouncedVisitorSearch = useDebounce(visitorSearch, 300);

  useEffect(() => {
    setVisitorsLoading(true);
    const params: Record<string, string> = { page: String(visitorsPage), pageSize: '20' };
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (debouncedVisitorSearch) params.search = debouncedVisitorSearch;
    apiFetch<VisitorsResponse>('visitors', params)
      .then(data => setVisitorsData(data))
      .catch(() => setVisitorsData(null))
      .finally(() => setVisitorsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef, dateFrom, dateTo, debouncedVisitorSearch, visitorsPage]);

  // Monthly chart
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [monthly, setMonthly] = useState<MonthlyStat[]>([]);
  const [monthlyLoading, setMonthlyLoading] = useState(true);

  useEffect(() => {
    setMonthlyLoading(true);
    apiFetch<MonthlyStat[]>('monthly', { year: String(selectedYear) })
      .then(data => setMonthly(data))
      .catch(() => setMonthly([]))
      .finally(() => setMonthlyLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef, selectedYear]);

  const maxMonthlyViews = Math.max(...monthly.map(m => m.views), 1);
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  // Top viewers
  const [topViewers, setTopViewers] = useState<TopViewerRow[]>([]);
  const [topViewersLoading, setTopViewersLoading] = useState(true);

  useEffect(() => {
    setTopViewersLoading(true);
    const params: Record<string, string> = { limit: '10' };
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    apiFetch<TopViewerRow[]>('top-viewers', params)
      .then(data => setTopViewers(data))
      .catch(() => setTopViewers([]))
      .finally(() => setTopViewersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityRef, dateFrom, dateTo]);

  // Helpers for date filter handlers (reset page on filter change)
  const handleDateFrom = useCallback((v: string) => { setDateFrom(v); setPagesPage(1); setVisitorsPage(1); }, []);
  const handleDateTo = useCallback((v: string) => { setDateTo(v); setPagesPage(1); setVisitorsPage(1); }, []);
  const handleSearch = useCallback((v: string) => { setPageSearch(v); setPagesPage(1); }, []);
  const handleVisitorSearch = useCallback((v: string) => { setVisitorSearch(v); setVisitorsPage(1); }, []);

  return (
    <Box mt={1}>
      {/* Date range filter row */}
      <Box display="flex" alignItems="center" mb={3} style={{ gap: 12, flexWrap: 'wrap' }}>
        <TextField
          label="From"
          type="date"
          size="small"
          value={dateFrom}
          onChange={e => handleDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 160 }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={dateTo}
          onChange={e => handleDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 160 }}
        />
        {(dateFrom || dateTo) && (
          <Button size="small" onClick={() => { handleDateFrom(''); handleDateTo(''); }}>
            Clear dates
          </Button>
        )}
      </Box>

      {/* Summary cards */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        {(() => {
          if (summaryLoading) return <Grid item xs={12}><Progress /></Grid>;
          if (summaryError) return (
            <Grid item xs={12}>
              <Typography color="error" variant="body2">{summaryError}</Typography>
            </Grid>
          );
          if (!summary) return null;
          return (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard icon={<VisibilityIcon />} label="Total page views" value={summary.totalViews.toLocaleString()} colour="#1976d2" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard icon={<LibraryBooksIcon />} label="Unique pages" value={summary.uniquePages.toLocaleString()} colour="#388e3c" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard icon={<PeopleIcon />} label="Unique visitors" value={summary.uniqueVisitors.toLocaleString()} colour="#f57c00" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SummaryCard icon={<AccessTimeIcon />} label="Last viewed" value={formatDate(summary.lastVisited)} colour="#7b1fa2" />
              </Grid>
            </>
          );
        })()}
      </Grid>

      <Grid container spacing={3}>
        {/* Pages table */}
        <Grid item xs={12} md={8}>
          <InfoCard
            title="Pages"
            noPadding
            action={
              <TextField
                placeholder="Search pages…"
                size="small"
                value={pageSearch}
                onChange={e => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: '#757575' }} />
                    </InputAdornment>
                  ),
                }}
                style={{ width: 220 }}
              />
            }
          >
            {(() => {
              if (pagesLoading) return <Box p={2}><Progress /></Box>;
              if (!pagesData || pagesData.pages.length === 0) return (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">
                    {pageSearch ? `No pages matching "${pageSearch}".` : 'No page views recorded yet.'}
                  </Typography>
                </Box>
              );
              return (
                <>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Page</TableCell>
                        <TableCell align="right">Views</TableCell>
                        <TableCell align="right">Unique visitors</TableCell>
                        <TableCell align="right">Last visited</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagesData.pages.map(p => (
                        <TableRow
                          key={p.pagePath}
                          hover
                          style={{ cursor: 'pointer' }}
                          onClick={() => onNavigatePage(p.pagePath)}
                        >
                          <TableCell>
                            <span style={{ color: '#e8442b' }}>{displayPage(p.pagePath)}</span>
                          </TableCell>
                          <TableCell align="right">{p.views.toLocaleString()}</TableCell>
                          <TableCell align="right">{p.uniqueVisitors.toLocaleString()}</TableCell>
                          <TableCell align="right">{formatDate(p.lastVisited)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                    <Typography variant="caption" color="text.secondary">
                      {(pagesData.page - 1) * PAGE_SIZE + 1}–{Math.min(pagesData.page * PAGE_SIZE, pagesData.total)} of {pagesData.total.toLocaleString()} pages
                    </Typography>
                    <Box>
                      <Button size="small" disabled={pagesPage <= 1} onClick={() => setPagesPage(p => p - 1)} startIcon={<NavigateBeforeIcon />}>Prev</Button>
                      <Button size="small" disabled={pagesPage * PAGE_SIZE >= pagesData.total} onClick={() => setPagesPage(p => p + 1)} endIcon={<NavigateNextIcon />}>Next</Button>
                    </Box>
                  </Box>
                </>
              );
            })()}
          </InfoCard>
        </Grid>

        {/* Monthly chart */}
        <Grid item xs={12} md={4}>
          <InfoCard
            title="Monthly views"
            action={
              <Box display="flex" style={{ gap: 4 }}>
                {yearOptions.map(y => (
                  <Chip key={y} label={y} size="small" color={y === selectedYear ? 'primary' : 'default'} onClick={() => setSelectedYear(y)} clickable />
                ))}
              </Box>
            }
          >
            {(() => {
              if (monthlyLoading) return <Progress />;
              if (monthly.length === 0) return <Typography variant="body2" color="text.secondary">No data for {selectedYear}.</Typography>;
              return (
                <Box pt={1}>
                  {monthly.map(m => (
                    <MonthlyBar key={m.month} label={m.month.slice(5)} views={m.views} maxViews={maxMonthlyViews} />
                  ))}
                  <Divider style={{ margin: '12px 0 8px' }} />
                  <Box display="flex" alignItems="center">
                    <BarChartIcon style={{ fontSize: 14, color: '#757575', marginRight: 4 }} />
                    <Typography variant="caption" color="text.secondary">
                      {monthly.reduce((s, m) => s + m.views, 0).toLocaleString()} total views in {selectedYear}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </InfoCard>
        </Grid>

        {/* Recent visitors */}
        <Grid item xs={12} md={8}>
          <InfoCard
            title="Recent visitors"
            noPadding
            action={
              <TextField
                placeholder="Search users…"
                size="small"
                value={visitorSearch}
                onChange={e => handleVisitorSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ fontSize: 18, color: '#757575' }} />
                    </InputAdornment>
                  ),
                }}
                style={{ width: 220 }}
              />
            }
          >
            {(() => {
              if (visitorsLoading) return <Box p={2}><Progress /></Box>;
              if (!visitorsData || visitorsData.visitors.length === 0) return (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">
                    {visitorSearch ? `No visitors matching "${visitorSearch}".` : 'No visitors recorded yet.'}
                  </Typography>
                </Box>
              );
              return (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Page</TableCell>
                      <TableCell align="right">When</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visitorsData.visitors.map((v, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Tooltip title={v.userRef}>
                            <span
                              role="button"
                              tabIndex={0}
                              style={{ color: '#e8442b', cursor: 'pointer' }}
                              onClick={() => onNavigateUser(v.userRef)}
                              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onNavigateUser(v.userRef); }}
                            >
                              {displayUser(v.userRef)}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <span
                            role="button"
                            tabIndex={0}
                            style={{ color: '#e8442b', cursor: 'pointer' }}
                            onClick={() => onNavigatePage(v.pagePath)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onNavigatePage(v.pagePath); }}
                          >
                            {displayPage(v.pagePath)}
                          </span>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={new Date(v.visitedAt).toLocaleString()}>
                            <span>{formatRelative(v.visitedAt)}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1}>
                  <Typography variant="caption" color="text.secondary">
                    {(visitorsData.page - 1) * visitorsData.pageSize + 1}–{Math.min(visitorsData.page * visitorsData.pageSize, visitorsData.total)} of {visitorsData.total.toLocaleString()} visits
                  </Typography>
                  <Box>
                    <Button size="small" disabled={visitorsPage <= 1} onClick={() => setVisitorsPage(p => p - 1)} startIcon={<NavigateBeforeIcon />}>Prev</Button>
                    <Button size="small" disabled={visitorsPage * visitorsData.pageSize >= visitorsData.total} onClick={() => setVisitorsPage(p => p + 1)} endIcon={<NavigateNextIcon />}>Next</Button>
                  </Box>
                </Box>
              </>
              );
            })()}
          </InfoCard>
        </Grid>

        {/* Top viewers */}
        <Grid item xs={12} md={4}>
          <InfoCard title="Top viewers" noPadding>
            {(() => {
              if (topViewersLoading) return <Box p={2}><Progress /></Box>;
              if (topViewers.length === 0) return (
                <Box p={3}>
                  <Typography variant="body2" color="text.secondary">No viewers recorded yet.</Typography>
                </Box>
              );
              const maxVisits = topViewers[0].totalVisits;
              return (
                <Box>
                  {topViewers.map((v, i) => (
                    <Box
                      key={v.userRef}
                      px={2}
                      py={1}
                      style={{ borderBottom: i < topViewers.length - 1 ? '1px solid rgba(0,0,0,0.06)' : undefined }}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                        <Box display="flex" alignItems="center" style={{ gap: 6, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" style={{ width: 16, textAlign: 'right', flexShrink: 0 }}>
                            {i + 1}
                          </Typography>
                          <span
                            role="button"
                            tabIndex={0}
                            style={{ color: '#e8442b', cursor: 'pointer', fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            onClick={() => onNavigateUser(v.userRef)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onNavigateUser(v.userRef); }}
                          >
                            {displayUser(v.userRef)}
                          </span>
                        </Box>
                        <Typography variant="caption" style={{ fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>
                          {v.totalVisits.toLocaleString()} view{v.totalVisits !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Box style={{ background: 'rgba(0,0,0,0.07)', borderRadius: 3, height: 4 }}>
                        <Box style={{ width: `${(v.totalVisits / maxVisits) * 100}%`, background: '#1976d2', borderRadius: 3, height: 4 }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// DocInsightsPage — navigation controller
// ---------------------------------------------------------------------------

export const DocInsightsPage = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const kind = entity.kind.toLowerCase();
  const namespace = entity.metadata.namespace ?? 'default';
  const name = entity.metadata.name;
  const entityRef = stringifyEntityRef(entity);
  const entityParams: EntityParams = { kind, namespace, name };

  // URL-based navigation: view state lives in query params so browser
  // Back / Forward and link-sharing work naturally.
  //   ?view=page&path=/some/page
  //   ?view=user&user=user:default/paul
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const view = searchParams.get('view') ?? 'main';
  const pagePathParam = searchParams.get('path') ?? '';
  const userRefParam = searchParams.get('user') ?? '';
  const fromPageParam = searchParams.get('fromPage') ?? '';

  function navigateToPage(pagePath: string) {
    setSearchParams({ view: 'page', path: pagePath });
  }

  function navigateToUser(userRef: string, fromPage?: string) {
    const params: Record<string, string> = { view: 'user', user: userRef };
    if (fromPage) params.fromPage = fromPage;
    setSearchParams(params);
  }

  function navigateToMain() {
    setSearchParams({});
  }

  // navigate(-1) steps back in browser history — works with the browser
  // Back button and matches what every official Backstage plugin does.
  function navigateBack() {
    navigate(-1);
  }

  // Shared fetch function — scoped to this entity
  const apiFetch = useCallback(
    async <T,>(path: string, params?: Record<string, string>): Promise<T> => {
      const baseUrl = await discoveryApi.getBaseUrl('doc-analytics');
      const url = new URL(`${baseUrl}/entity/${kind}/${namespace}/${name}/${path}`);
      if (params) {
        Object.entries(params).forEach(([k, v]) => { if (v) url.searchParams.set(k, v); });
      }
      const res = await fetchApi.fetch(url.toString());
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return res.json() as Promise<T>;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityRef],
  );

  if (view === 'page' && pagePathParam) {
    return (
      <PageDetailView
        pagePath={pagePathParam}
        entity={entityParams}
        apiFetch={apiFetch}
        onNavigateUser={navigateToUser}
        onBack={navigateBack}
        onGoToMain={navigateToMain}
        entityName={name}
      />
    );
  }

  if (view === 'user' && userRefParam) {
    return (
      <UserDetailView
        userRef={userRefParam}
        fromPage={fromPageParam || undefined}
        apiFetch={apiFetch}
        onNavigatePage={navigateToPage}
        onBack={navigateBack}
        onGoToMain={navigateToMain}
        entityName={name}
      />
    );
  }

  return (
    <MainView
      apiFetch={apiFetch}
      entityRef={entityRef}
      onNavigatePage={navigateToPage}
      onNavigateUser={navigateToUser}
    />
  );
};

